import { clip, type Clip } from '../clip.ts';
import { TAU, inCubic, inOut, outCubic, outExpo, outQuad, phase, settle } from '../curves.ts';
import { timeline, type KeyPose } from '../pose.ts';
import { sentinelClips } from './sentinel.ts';

/*
 * Ser Corvin, the Knight of the Gate (models/corvin.ts, docs/ANTIHEROES.md): built on the fallen sentinel's bones,
 * so he walks, reels, staggers and falls as the sentinel does. His own: the sword's cleave (attack, landing at
 * 0.13 s like every nightmare's blow), the challenge (the blade levelled at a hero), the channel of the Night of
 * the Gate (the sword raised high, shaking, held as long as the sim holds him) and the Night itself, a blow that
 * brings the sword down into the ground with his whole weight (0.18 s).
 *
 * Conventions as in sentinel.ts: −X on an arm swings it forward and up, −X on a forearm bends the elbow, +X on
 * the trunk leans it forward, +Y turns the trunk to its left (the shield shoulder comes forward).
 */

type B =
  | 'root' | 'body' | 'chest' | 'head' | 'flapF' | 'flapB'
  | 'armL' | 'foreL' | 'shield' | 'armR' | 'foreR' | 'sword'
  | 'legL' | 'shinL' | 'legR' | 'shinR';
type P = KeyPose<B>;

const kick = (t: number, t0: number, freq = 2.5, damp = 7): number => (t < t0 ? 0 : settle(t - t0, freq, damp));
const thud = (t: number, hits: readonly [number, number][]): [number, number, number] => {
  let k = 0;
  for (const [t0, a] of hits) k += a * Math.max(0, kick(t, t0, 3, 9));
  return [1 + 0.6 * k, 1 - k, 1 + 0.6 * k];
};

/** The sword up and back over the right shoulder, the shield turned in: the cleave's wind-up. */
const RAISED: P = {
  root: [0, 0.1, 0, 0, -0.02, -0.05],
  body: [-0.04, -0.2, 0],
  chest: [-0.12, -0.35, 0.05],
  head: [0.02, 0.3, 0],
  armR: [-2.5, 0, -0.35],
  foreR: [-0.7, 0, 0],
  sword: [-0.5, 0, 0],
  armL: [0.25, 0, 0.1],
  legL: [-0.2, 0, -0.05],
  shinL: [0.2, 0, 0],
  legR: [0.25, 0, 0.05],
  shinR: [0.35, 0, 0],
};
/** The cleave lands: the blade down and across, the weight over the front foot. */
const CLEFT: P = {
  root: [0, -0.15, 0, 0, -0.06, 0.16],
  body: [0.16, 0.25, 0],
  chest: [0.25, 0.45, -0.05],
  head: [0.1, -0.35, 0],
  armR: [-0.35, 0, 0.35],
  foreR: [-0.15, 0, 0],
  sword: [0.6, 0, 0],
  armL: [0.15, 0, 0.15],
  legL: [-0.55, 0, -0.05],
  shinL: [0.45, 0, 0],
  legR: [0.4, 0, 0.05],
  shinR: [0.15, 0, 0],
  flapF: [-0.35, 0, 0],
  flapB: [0.25, 0, 0],
};

const attack = (): Clip<B> =>
  clip<B>('attack', 1.1, false, (b, t) => {
    timeline(b, t, [
      [0, RAISED],
      [0.05, { ...RAISED, armR: [-2.65, 0, -0.4] }, outQuad],
      [0.13, CLEFT, inCubic],
      [0.4, CLEFT, outCubic],
      [1.1, {}, inOut],
    ], { head: 0.04, flapF: 0.08, flapB: 0.08 });
    b.root.pos(0, 0, -0.02 * kick(t, 0.13, 6, 9));
  }, (t) => thud(t, [[0.13, 0.04]]), 'Tajo');

/** The challenge: the blade levelled at a hero, held, then brought back to guard. */
const challenge = (): Clip<B> =>
  clip<B>('challenge', 1.4, false, (b, t) => {
    const point: P = { body: [0, 0.15, 0], chest: [-0.05, 0.35, 0], head: [-0.08, -0.3, 0], armR: [-1.55, 0, 0.05], foreR: [0.05, 0, 0], sword: [-1.5, 0, 0], armL: [0.3, 0, 0.15], legL: [-0.2, 0, 0], legR: [0.15, 0, 0] };
    timeline(b, t, [
      [0, {}],
      [0.22, point, outExpo],
      [1.0, point],
      [1.4, {}, inOut],
    ], { head: 0.05 });
  }, undefined, 'Reto');

/** The Night of the Gate, channelled: the sword raised high in both the arm and the will, shaking with it. */
const channel = (): Clip<B> =>
  clip<B>('channel', 1.2, true, (b, t) => {
    const p = phase(t, 1.2) * TAU;
    const shake = Math.sin(p * 7) * 0.03;
    b.root.pos(0, -0.04 + 0.01 * Math.sin(p * 2), 0).rot(-0.06, 0, 0);
    b.body.rot(-0.1, 0, 0);
    b.chest.rot(-0.22 + shake, 0.05 * Math.sin(p), 0);
    b.head.rot(-0.35, 0, 0);
    b.armR.rot(-2.95 + shake, 0, -0.15);
    b.foreR.rot(-0.1);
    b.sword.rot(-0.15 + shake * 2);
    b.armL.rot(0.45, 0, 0.35);
    b.foreL.rot(0.25);
    b.shield.rot(0.25, 0, -0.1);
    b.legL.rot(-0.25, 0, -0.08);
    b.shinL.rot(0.3);
    b.legR.rot(0.25, 0, 0.08);
    b.shinR.rot(0.35);
    b.flapF.rot(0.1 * Math.sin(p * 3));
    b.flapB.rot(-0.12 + 0.1 * Math.sin(p * 3 + 1));
  }, (t) => {
    const k = Math.sin(phase(t, 1.2) * TAU * 7) * 0.006;
    return [1 + k, 1 - k, 1 + k];
  }, 'Canaliza');

/** The Night lands: from high overhead, the sword driven into the ground in front of him. */
const night = (): Clip<B> =>
  clip<B>('night', 1.6, false, (b, t) => {
    const high: P = { root: [0, 0, 0, 0, -0.04, 0], chest: [-0.22, 0, 0], head: [-0.35, 0, 0], armR: [-2.95, 0, -0.15], foreR: [-0.1, 0, 0], sword: [-0.15, 0, 0], armL: [0.45, 0, 0.35], shield: [0.25, 0, -0.1], legL: [-0.25, 0, -0.08], shinL: [0.3, 0, 0], legR: [0.25, 0, 0.08], shinR: [0.35, 0, 0] };
    const down: P = { root: [0, 0, 0, 0, -0.22, 0.22], body: [0.35, 0, 0], chest: [0.45, 0, 0], head: [0.2, 0, 0], armR: [-0.7, 0, 0.1], foreR: [-0.05, 0, 0], sword: [1.0, 0, 0], armL: [0.2, 0, 0.3], shield: [0.1, 0, 0], legL: [-0.8, 0, -0.05], shinL: [1.0, 0, 0], legR: [0.55, 0, 0.05], shinR: [0.6, 0, 0], flapF: [-0.5, 0, 0], flapB: [0.45, 0, 0] };
    timeline(b, t, [
      [0, high],
      [0.18, down, inCubic],
      [0.8, down],
      [1.6, {}, inOut],
    ], { head: 0.05, flapF: 0.08, flapB: 0.08 });
    b.root.pos(0.02 * kick(t, 0.18, 7, 6), 0, 0);
  }, (t) => thud(t, [[0.18, 0.09]]), 'La Noche del Portón');

/** Clips by library kind (see ../rigs.ts): the sentinel's, with his own blow and powers in place of the bash. */
export const corvinClips = (): Clip[] => [...sentinelClips().filter((c) => c.name !== 'attack'), attack(), challenge(), channel(), night()] as Clip[];
export const CLIPS: Record<string, () => Clip[]> = { corvin: corvinClips };
